import React, { useMemo } from 'react';
import { Button } from 'src/components/design-system';
import { EVENTS } from 'shared/constants/analytics';
import type { HubOffer } from 'shared/panda/messages/hub-offer';
import { hubAnalyticsType } from 'shared/panda/analytics/panda-analytics';
import { useAnalyticsImpression } from 'src/hooks/analytics/use-analytics-impression';
import { usePandaConversation } from '../../panda-chat-provider';
import { pandaAnalytics } from '../../panda-analytics';
import styles from './hub-offer.module.scss';

interface HubOfferButtonProps {
  offer: HubOffer;
  /** The assistant message carrying the offer (analytics). */
  messageId: string;
}

// "Show ASA guidance" — accepts the knowledge-hub (ASA) offer, starting the
// hub-generation run. Disabled while a run is in flight: doubles as the v1 guard
// against a cross-replica double-accept (the backend has no atomic consume yet).
export function HubOfferButton({ offer, messageId }: HubOfferButtonProps) {
  const { conversationId, onOfferHubAnswer, loading, lang } = usePandaConversation();

  // Impression + click share the offer id as impression_id, so the two join.
  const eventProperties = useMemo(
    () => ({
      conversationId,
      messageId,
      impression_id: offer.offerId,
      type: hubAnalyticsType(offer.source),
    }),
    [conversationId, messageId, offer.offerId, offer.source],
  );
  const { ref: impressionRef } = useAnalyticsImpression({
    event: EVENTS.AI_CHAT.FALLBACK_IMPRESSION,
    eventPayload: eventProperties,
    impressionId: offer.offerId,
  });

  const onClick = () => {
    pandaAnalytics.trackFallbackClick(eventProperties);
    onOfferHubAnswer(offer.offerId, offer.chunkIds);
  };

  return (
    <span ref={impressionRef} className={styles.impression}>
      <Button className={styles.button} disabled={loading} onClick={onClick}>
        {lang.ASA_SHOW_GUIDANCE}
      </Button>
    </span>
  );
}
